// src/services/userService.ts
import apiClient from './api';
import axios from 'axios'; // <-- Keep axios import for isAxiosError check
import logger from '../utils/logger';
import { firebaseAuth } from '../config/firebase';
import { AppUser } from '../contexts/AuthContext';

interface UserServiceOutput {
  deviceId: string | null;  // acks the device ID that it received
  timestamp: string | null; // when the service saved away users' input
}

const API_BASE_URL = process.env.REACT_APP_PULSE_API_BASE_URL || 'http://localhost:8080';

interface FetchOptionParams {
  method: string;
  headers?: HeadersInit;
  queryParams?: URLSearchParams;
  body?: string;
}

class UserService {

  /**
   * Associate authToken and deviceId in the service side
   *
   * @param {string | null} authToken - The raw auth token received from the upstream authentication service.
   * @param {string} deviceId - Device specific user ID
   * @returns {Promise<UserServiceOutput>}
   *          An object with 'deviceId' and 'timestamp' properties.
   */
  associateDeviceWithUser = async (authToken: string | null, deviceId: string): Promise<UserServiceOutput> => {

    if (!authToken) {

      logger.error(" Unable to fund the user token.");
      throw new Error('No Auth token is provided for associating device ID.');
    }
      
    try {
      // Use apiClient.post and provide only the relative path
      const response = await apiClient.post('/api/users/associate-device', {
        // Request body
        deviceId
      }, {
        // Request config (headers)
        headers: {
          // Content-Type is likely already set in apiClient, but Authorization needs to be added
          'Authorization': `Bearer ${authToken}`,
        },
      });

      // Response handling remains the same
      if (response.data && typeof response.data.deviceId === 'string') {
        // No content expected on success, or handle response data if needed
        // logger.debug( `associate-device: id ${response.data.deviceId} registered at ${response.data.timestamp}`);
        return { deviceId: response.data.deviceId, timestamp: response.data.timestamp }
      } else {
        throw new Error('Invalid response format from user API to associate device.');
      }
    } catch (error: any) {
      logger.error('Error associating device with the service:', error);

      // Provide a more user-friendly error message or re-throw
      if (axios.isAxiosError(error) && error.response) {
        // Handle specific API errors if needed
        throw new Error(`API Error: ${error.response.status} - ${error.response.data?.message || error.message}`);
      }

      throw new Error('Failed to associate device id. Please check the connection or try again.');
    }
  }


  // Function to get the ID token (could be a helper)
  async getCurrentUserToken(): Promise<string | null> {
    const currentUser = firebaseAuth.currentUser;
    if (!currentUser) {
        logger.warn("No user currently signed in.");
        return null;
    }
    try {
        const token = await currentUser.getIdToken();
        return token;
    } catch (error) {
        logger.error("Error getting ID token in service:", error);
        return null;
    }
  }

  /**
   * Get my user profile
   *
   * @returns {Promise<AppUser>}
   *          An object with user profile data
   */
  getMyProfile = async (): Promise<AppUser> => {

    try {
/*      
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("User not logged in.");
      }

      const idToken = await user.getIdToken(); // Get the current user's ID token
*/
      const idToken = await this.getCurrentUserToken();

      if (!idToken) {
        // Handle case where user is not logged in or token couldn't be retrieved
        throw new Error("Authentication token not available.");
      }      

      const response = await apiClient.get('/users/me',
        {
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json' // Optional for GET, but good practice
          },
        }
      );

      console.log("Response from /users/me:", response);

      // Response handling remains the same
      if (response.data && typeof response.data.name === 'string') {
        return response.data; // is that return data in the right format? JSON?
      } else {
        throw new Error('Invalid response format from getMyProfile API.');
      }
    } catch (error: any) {
      logger.error('Error getting profile information:', error);

      // Provide a more user-friendly error message or re-throw
      if (axios.isAxiosError(error) && error.response) {
        // Handle specific API errors if needed
        throw new Error(`API Error: ${error.response.status} - ${error.response.data?.message || error.message}`);
      }

      throw new Error('Failed to get user profile information.');
    }
  } // getMyProfile()


  // Helper function for making authenticated GET requests
  authenticatedFetch = async <T>(
    purpose: string, // For logging and error messages
    apiKey: string | null,
    method: string = "GET",
    endpoint: string,
    queryParams?: URLSearchParams,
    bodyParams?: string
  ): Promise<T> => {
    
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      "X-API-KEY": apiKey || process.env['REACT_APP_PULSE_API_KEY'] || ''
    };
  
    try {
      const userToken = await userService.getCurrentUserToken();
      if (!userToken) {
        logger.warn(`Could not retrieve user token. Unable to fetch ${purpose}.`);
        throw new Error(`User token is null or undefined; cannot fetch ${purpose}.`);
      }
      headers['Authorization'] = `Bearer ${userToken}`;
  
      const apiUrl = `${API_BASE_URL}${endpoint}?${queryParams?.toString()}`;
      // logger.debug(`Fetching ${purpose} from: ${apiUrl}`);
  
      const response = await fetch(apiUrl, {
        method: method,
        headers: headers,
        body: bodyParams ?? undefined,
      });
  
      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`API Error fetching ${purpose}: ${response.status} - ${errorText}`, { status: response.status, url: apiUrl });
        throw new Error(`Failed to fetch ${purpose}: ${response.status} ${response.statusText}. Details: ${errorText}`);
      }
  
      const data: T = await response.json();
      // Basic check if data is not null/undefined. More specific checks can be done by the caller.
      if (data === null || data === undefined) {
          logger.error(`${purpose} data not found or malformed in API response (null/undefined)`, { responseData: data, url: apiUrl });
          throw new Error(`${purpose} data not found or malformed in API response (null/undefined)`);
      }
      return data;
  
    } catch (error: any) {
      // Log the specific error for this attempt
      logger.error(`Error in authenticatedFetch while fetching ${purpose}:`, error.message);
      // Re-throw the error so the calling function can handle it or it propagates further
      throw error;
    }
  }


  authenticatedFetch2 = async <T>(
      purpose: string, // For logging and error messages
      apiKey: string | null,
      endpoint: string,
      options: FetchOptionParams,
    ): Promise<T> => {

      return this.authenticatedFetch<T>(
        purpose,
        apiKey,
        options.method,
        endpoint,
        options.queryParams,
        options.body
      );
  }

  // Add other service methods here, using apiClient.get, apiClient.put, etc.
}

// Create a singleton instance of the service
const userService = new UserService();

export default userService;
